import { TagTopic, UserAvatar } from '@/components'
import { RoundedFilledButton, RoundedOutlineButton } from '@/components/StyledComponents'
import colors from '@/styles/colors'
import { Topic } from '@/types'
import { PrivacyTypes, TopicTypes } from '@/utils/constants'
import { convertToGMT } from '@/utils/helper'
import { ArrowBackIos, EditOutlined, MoreVert } from '@mui/icons-material'
import { Box, IconButton, Menu, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material'
import { styled, SxProps } from '@mui/system'
import { useLocalStorage } from '@/hooks/common'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import ReactTimeago from 'react-timeago'
import MenuIcon from '@mui/icons-material/Menu'
import { useState } from 'react'
import { RBox } from '@/pages/__styled'
import CustomDivider from '@/components/CustomDivider'

interface PreviewTopBarProps {
  topic: Topic
  showingDetailsInPanel: boolean
  showTopicInfo: boolean
  onClickMenuIcon: () => void
  onClickBack: () => void
  onClickExitPreview: () => void
  onClickEdit: () => void
  isLiked: boolean
  onClickLike: () => void
  onClickComment: () => void
  onClickPreview: () => void
  onClickLikesCount: () => void
  onClickShares: () => void
}

export const IconsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  pt: '16px',
  pb: '4px',
}))

export const PreviewTopBar = ({
  topic,
  showingDetailsInPanel = false,
  showTopicInfo = true,
  onClickMenuIcon,
  onClickBack,
  onClickExitPreview,
  onClickEdit,
  isLiked,
  onClickLike,
  onClickComment,
  onClickPreview,
  onClickLikesCount,
  onClickShares,
}: PreviewTopBarProps) => {
  const styles = makeStyles()
  const [ewUser] = useLocalStorage('ewUser', null)
  const theme = useTheme()
  const lessThanMedium = useMediaQuery(theme.breakpoints.down('lg'))
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)

  const totalViews = Number(topic.visitorVisits)
  const totalLikes = Number(topic.totalLikes)
  const totalComments = Number(topic.totalComments)
  const isMyTopic = ewUser?.userID == topic.userID

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = () => {
    setAnchorElNav(null)
  }

  const renderTags = () => (
    <RBox gap={'10px'}>
      <TagTopic type={`${TopicTypes[topic.topicType]?.title || 'Unknown'}`} textColor='white' />
      <TagTopic type={`${PrivacyTypes[Number(topic.visibility)].label}`} textColor='white' />
    </RBox>
  )

  return (
    <Box
      sx={{
        height: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 4,
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Left Section: Back button + User info + Document title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
        <IconButton
          onClick={onClickBack}
          sx={{
            p: '10px',
            width: '44px',
            height: '44px',
            color: 'white',

            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              transform: 'translateX(-2px)',
            },
            '&:active': {
              transform: 'translateX(-4px) scale(0.98)',
            },
          }}
        >
          <ArrowBackIos sx={{ fontSize: 18 }} style={{ marginLeft: '6px' }} />
        </IconButton>

        {showTopicInfo && !showingDetailsInPanel && (
          <>
            <Box
              sx={{
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '-2px',
                  borderRadius: '50%',
                  padding: '2px',
                  background:
                    'linear-gradient(135deg, rgba(99, 102, 241, 0.5), rgba(168, 85, 247, 0.5))',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                },
              }}
            >
              <UserAvatar
                name={topic.userName}
                url={topic.profilePicThumbURL}
                size={44}
                borderRadius={22}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography
                variant='h6'
                sx={{
                  color: 'white',
                  fontSize: '17px',
                  fontWeight: 700,
                  lineHeight: '22px',
                  letterSpacing: '-0.02em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px',
                }}
              >
                {topic.topicName}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '18px',
                  mt: '2px',
                }}
              >
                {ewUser?.userID == topic.userID ? 'You' : topic.userName} •{' '}
                <ReactTimeago date={convertToGMT(topic.dateAdded)} />
              </Typography>
            </Box>

            {/* Tags */}
            <Box sx={{ display: 'flex', gap: '10px', ml: 3 }}>
              <Box
                sx={{
                  px: '16px',
                  py: '8px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    borderColor: 'rgba(255, 255, 255, 0.25)',
                  },
                }}
              >
                {TopicTypes[topic.topicType]?.title || 'Pattern'}
              </Box>
              <Box
                sx={{
                  px: '16px',
                  py: '8px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                {PrivacyTypes[Number(topic.visibility)].label}
              </Box>
            </Box>
          </>
        )}

        {/* Mobile menu icon */}
        {lessThanMedium && !showingDetailsInPanel && (
          <IconButton
            onClick={onClickMenuIcon}
            sx={{
              width: '44px',
              height: '44px',
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Show tags when in details panel */}
        {showingDetailsInPanel && renderTags()}
      </Box>

      {/* Right Section: Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {!showingDetailsInPanel && !lessThanMedium && (
          <>
            {/* Like Button */}
            <IconButton
              onClick={onClickLike}
              sx={{
                px: '16px',
                py: '10px',
                minHeight: '44px',
                borderRadius: '12px',
                backgroundColor: isLiked ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                border: `1px solid ${
                  isLiked ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.1)'
                }`,
                color: 'white',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(8px)',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  borderColor: 'rgba(99, 102, 241, 0.3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.98)',
                },
                minWidth: '70px',
                gap: '8px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <SVG
                src={isLiked ? SVGS.LikeFilledIcon : SVGS.LikeWhiteIcon}
                width={18}
                height={18}
                style={{ filter: isLiked ? 'brightness(0) invert(1)' : 'none' }}
              />
              <Typography
                sx={{ fontSize: '14px', fontWeight: 600, color: 'white', letterSpacing: '-0.01em' }}
              >
                {totalLikes}
              </Typography>
            </IconButton>

            {/* Comment Button */}
            <IconButton
              onClick={onClickComment}
              sx={{
                px: '16px',
                py: '10px',
                minHeight: '44px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(8px)',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  borderColor: 'rgba(99, 102, 241, 0.3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.98)',
                },
                minWidth: '70px',
                gap: '8px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <SVG src={SVGS.CommentWhiteIcon} width={18} height={18} />
              <Typography
                sx={{ fontSize: '14px', fontWeight: 600, color: 'white', letterSpacing: '-0.01em' }}
              >
                {totalComments}
              </Typography>
            </IconButton>

            {/* Views Button */}
            <IconButton
              sx={{
                px: '16px',
                py: '10px',
                minHeight: '44px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(8px)',
                pointerEvents: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  borderColor: 'rgba(99, 102, 241, 0.3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.98)',
                },
                minWidth: '70px',
                gap: '8px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <SVG src={SVGS.EyeIconWhite} width={18} height={18} />
              <Typography
                sx={{ fontSize: '14px', fontWeight: 600, color: 'white', letterSpacing: '-0.01em' }}
              >
                {totalViews}
              </Typography>
            </IconButton>

            {/* Share Button */}
            <IconButton
              onClick={onClickShares}
              sx={{
                px: '16px',
                py: '10px',
                minHeight: '44px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(8px)',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  borderColor: 'rgba(99, 102, 241, 0.3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.98)',
                },
                minWidth: '70px',
                gap: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SVG src={SVGS.ShareWhiteIcon} width={18} height={18} />
              {/*<Typography*/}
              {/*  sx={{ fontSize: '14px', fontWeight: 600, color: 'white', letterSpacing: '-0.01em' }}*/}
              {/*>*/}
              {/*  1*/}
              {/*</Typography>*/}
            </IconButton>
          </>
        )}

        {/* Edit Button */}
        {!showingDetailsInPanel && isMyTopic && (
          <Box
            component='button'
            onClick={onClickEdit}
            sx={{
              px: '24px',
              py: '12px',
              minHeight: '44px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              border: 'none',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
                background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
              },
              '&:active': {
                transform: 'translateY(0px) scale(0.98)',
              },
            }}
          >
            Edit
          </Box>
        )}

        {/* Mobile menu */}
        {lessThanMedium && (
          <IconButton
            onClick={handleOpenNavMenu}
            sx={{
              width: '44px',
              height: '44px',
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
            }}
          >
            <MoreVert />
          </IconButton>
        )}

        <Menu
          anchorEl={anchorElNav}
          open={Boolean(anchorElNav)}
          onClose={handleCloseNavMenu}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '12px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <MenuItem
            onClick={() => {
              onClickLike()
              handleCloseNavMenu()
            }}
          >
            <Typography>Like ({totalLikes})</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onClickComment()
              handleCloseNavMenu()
            }}
          >
            <Typography>Comment ({totalComments})</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onClickShares()
              handleCloseNavMenu()
            }}
          >
            <Typography>Share</Typography>
          </MenuItem>
          {isMyTopic && (
            <MenuItem
              onClick={() => {
                onClickEdit()
                handleCloseNavMenu()
              }}
            >
              <Typography>Edit</Typography>
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  )
}

function makeStyles(): Record<string, SxProps> {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      py: '16px',
      px: '20px',
    },

    interactionBar: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: '12px',
    },

    iconsContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      pt: '16px',
      pb: '4px',
    },

    outlineButton: {
      px: '22px',
      py: '8px',
      backgroundColor: '#FFFFFF33',
      borderWidth: '0px',
      color: 'white',
      ':hover': {
        borderWidth: '0px',
        backgroundColor: colors.meshdomBlue,
      },
    },

    row: { display: 'flex', flexDirection: 'row' },

    viewsDiv: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
    },
  }
}
